import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import {
  Search,
  Upload,
  Download,
  UserPlus,
  Eye,
  Edit,
  Users,
  Settings,
  ChevronDown,
  User,
  Crown,
  Award,
  Star,
  Trophy,
} from 'lucide-react';

// Rank configuration with colors matching reference design
const RANK_CONFIG = {
  agent: { label: 'Agent', color: 'bg-gray-100 text-gray-600', badgeColor: 'bg-gray-500', icon: User },
  neg: { label: 'Agent', color: 'bg-gray-100 text-gray-600', badgeColor: 'bg-gray-500', icon: User },
  rm: { label: 'RM', color: 'bg-purple-100 text-purple-600', badgeColor: 'bg-purple-500', icon: Award },
  avp: { label: 'AVP', color: 'bg-blue-100 text-blue-600', badgeColor: 'bg-blue-500', icon: Star },
  svp: { label: 'SVP', color: 'bg-green-100 text-green-600', badgeColor: 'bg-green-500', icon: Crown },
  evp: { label: 'EVP', color: 'bg-pink-100 text-pink-600', badgeColor: 'bg-pink-500', icon: Trophy },
};

const STATUS_CONFIG = {
  active: { label: 'active', color: 'bg-green-50 text-green-600 border border-green-200' },
  inactive: { label: 'inactive', color: 'bg-gray-50 text-gray-500 border border-gray-200' },
  pending: { label: 'pending', color: 'bg-yellow-50 text-yellow-600 border border-yellow-200' },
};

// Avatar colors for consistent display
const AVATAR_COLORS = [
  '#F472B6', '#A78BFA', '#60A5FA', '#34D399', '#FBBF24', '#FB7185', '#818CF8', '#2DD4BF'
];

const getAvatarColor = (name) => {
  const index = (name?.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const AgentManagement = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rankFilter, setRankFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [showEditAgent, setShowEditAgent] = useState(false);
  const [showChangeHierarchy, setShowChangeHierarchy] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    loadData();
  }, [search, rankFilter, statusFilter]);

  const loadData = async () => {
    try {
      const res = await api.get('/users');
      const users = res.data;
      setAllUsers(users);
      
      // Filter based on search and filters
      let filtered = users.filter(u => 
        ['agent', 'neg', 'rm', 'avp', 'svp', 'evp'].includes(u.role)
      );
      
      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(u => 
          u.full_name?.toLowerCase().includes(searchLower) ||
          u.agent_code?.toLowerCase().includes(searchLower) ||
          u.email?.toLowerCase().includes(searchLower)
        );
      }
      
      if (rankFilter && rankFilter !== 'all') {
        filtered = filtered.filter(u => u.agent_rank === rankFilter || u.role === rankFilter);
      }
      
      if (statusFilter && statusFilter !== 'all') {
        filtered = filtered.filter(u => u.status === statusFilter);
      }
      
      setAgents(filtered);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAgents(agents.map(a => a.id));
    } else {
      setSelectedAgents([]);
    }
  };

  const handleSelectAgent = (agentId, checked) => {
    if (checked) {
      setSelectedAgents([...selectedAgents, agentId]);
    } else {
      setSelectedAgents(selectedAgents.filter(id => id !== agentId));
    }
  };

  const handleViewAgent = (agent) => {
    navigate(`/admin/agents/${agent.id}`);
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setShowEditAgent(true);
  };

  const handleChangeHierarchy = (agent) => {
    setSelectedAgent(agent);
    setShowChangeHierarchy(true);
  };

  const handleExport = () => {
    const csvContent = [
      ['Agent Code', 'Full Name', 'Email', 'Phone', 'Rank', 'Status', 'Total Earned'].join(','),
      ...agents.map(a => [
        a.agent_code,
        a.full_name,
        a.email,
        a.phone,
        a.agent_rank || a.role,
        a.status,
        a.total_earned || 0
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agents_export.csv';
    a.click();
    toast.success('Export completed');
  };

  const getRankBadge = (rank) => {
    const config = RANK_CONFIG[rank] || RANK_CONFIG.agent;
    return (
      <Badge className={`${config.color} font-medium px-3 py-1 rounded-full text-xs`}>
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    return (
      <Badge className={`${config.color} font-medium px-3 py-1 rounded-full text-xs`}>
        {config.label}
      </Badge>
    );
  };

  const getUplineInfo = (agent) => {
    if (!agent.upline_id) return null;
    return allUsers.find(u => u.id === agent.upline_id);
  };

  // Count downlines for an agent
  const getDownlineCount = (agentId) => {
    return allUsers.filter(u => u.upline_id === agentId).length;
  };

  return (
    <div className="p-6 space-y-6" data-testid="agent-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Management</h1>
          <p className="text-sm text-gray-500">Manage agents, uplines, and hierarchy</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => toast.info('Import feature coming soon')}>
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => setShowAddAgent(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Agent
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, code, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={rankFilter} onValueChange={setRankFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Ranks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ranks</SelectItem>
            <SelectItem value="agent">Agent</SelectItem>
            <SelectItem value="rm">RM</SelectItem>
            <SelectItem value="avp">AVP</SelectItem>
            <SelectItem value="svp">SVP</SelectItem>
            <SelectItem value="evp">EVP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Agents Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedAgents.length === agents.length && agents.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead className="text-gray-600 font-semibold">AGENT</TableHead>
              <TableHead className="text-gray-600 font-semibold">CONTACT</TableHead>
              <TableHead className="text-gray-600 font-semibold">RANK</TableHead>
              <TableHead className="text-gray-600 font-semibold">STATUS</TableHead>
              <TableHead className="text-gray-600 font-semibold">COMMISSION</TableHead>
              <TableHead className="text-gray-600 font-semibold">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full mx-auto" />
                </TableCell>
              </TableRow>
            ) : agents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                  No agents found
                </TableCell>
              </TableRow>
            ) : (
              agents.map((agent) => (
                <TableRow key={agent.id} className="hover:bg-gray-50" data-testid={`agent-row-${agent.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedAgents.includes(agent.id)}
                      onCheckedChange={(checked) => handleSelectAgent(agent.id, checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                        style={{ backgroundColor: getAvatarColor(agent.full_name) }}
                      >
                        {agent.full_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{agent.full_name}</p>
                        <p className="text-sm text-gray-500">{agent.agent_code}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-gray-900">{agent.email}</p>
                      <p className="text-sm text-gray-400">{agent.phone || 'N/A'}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getRankBadge(agent.agent_rank || agent.role)}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(agent.status || 'active')}
                  </TableCell>
                  <TableCell>
                    <span className="font-semibold text-gray-900">
                      RM {(agent.total_commission_earned || agent.total_earned || 0).toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                        onClick={() => handleViewAgent(agent)}
                        title="View Details"
                        data-testid={`view-agent-${agent.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                        onClick={() => handleEditAgent(agent)}
                        title="Edit Agent"
                        data-testid={`edit-agent-${agent.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-orange-600 hover:bg-orange-50"
                        onClick={() => handleChangeHierarchy(agent)}
                        title="Change Hierarchy"
                        data-testid={`hierarchy-agent-${agent.id}`}
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        title="Settings"
                        data-testid={`settings-agent-${agent.id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Agent Sheet */}
      <EditAgentSheet
        open={showEditAgent}
        onOpenChange={setShowEditAgent}
        agent={selectedAgent}
        allUsers={allUsers}
        onSave={() => {
          loadData();
          setShowEditAgent(false);
        }}
      />

      {/* Change Hierarchy Dialog */}
      <ChangeHierarchyDialog
        open={showChangeHierarchy}
        onOpenChange={setShowChangeHierarchy}
        agent={selectedAgent}
        allUsers={allUsers}
        onSave={() => {
          loadData();
          setShowChangeHierarchy(false);
        }}
      />

      {/* Add Agent Dialog */}
      <AddAgentDialog
        open={showAddAgent}
        onOpenChange={setShowAddAgent}
        allUsers={allUsers}
        onSave={() => {
          loadData();
          setShowAddAgent(false);
        }}
      />
    </div>
  );
};

// Edit Agent Sheet Component
const EditAgentSheet = ({ open, onOpenChange, agent, allUsers, onSave }) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agent) {
      setFormData({
        full_name: agent.full_name || '',
        nickname: agent.nickname || '',
        email: agent.email || '',
        phone: agent.phone || '',
        contact_number: agent.contact_number || '',
        ic_passport: agent.ic_passport || '',
        address: agent.address || '',
        bank_name: agent.bank_name || '',
        bank_account: agent.bank_account || '',
        mobile_wallet: agent.mobile_wallet || '',
        agent_rank: agent.agent_rank || agent.role || 'agent',
        status: agent.status || 'active',
        upline_id: agent.upline_id || '',
        referred_by_code: agent.referred_by_code || '',
        commission_percentage: agent.commission_percentage || 2.5,
        kyc_status: agent.kyc_status || 'pending',
        full_time_agent: agent.full_time_agent || false,
      });
    }
  }, [agent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${agent.id}`, formData);
      toast.success('Agent updated successfully');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  if (!agent) return null;

  const uplineOptions = allUsers.filter(u => 
    u.id !== agent.id && ['neg', 'rm', 'avp', 'svp', 'evp'].includes(u.role)
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-xl">
            <div>Edit Agent</div>
            <div className="text-sm font-normal text-gray-500">
              {agent.agent_code} - {agent.full_name}
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <User className="w-4 h-4 text-purple-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Full Name *</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Nickname</label>
                <Input
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder="Enter nickname"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email *</label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone *</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Contact Number</label>
                <Input
                  value={formData.contact_number}
                  onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">IC/Passport Number</label>
                <Input
                  value={formData.ic_passport}
                  onChange={(e) => setFormData({ ...formData, ic_passport: e.target.value })}
                  placeholder="Enter IC or passport number"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter full address"
              />
            </div>
          </div>

          {/* Banking Information */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <svg className="w-4 h-4 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 10h16v10H4V10zm0-4h16l-8-4-8 4zm2 6h2v6H6v-6zm4 0h2v6h-2v-6zm4 0h2v6h-2v-6z"/>
              </svg>
              Banking Information
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Bank Name</label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="e.g., Maybank"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Bank Account</label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                  placeholder="Account number"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Mobile Wallet</label>
                <Input
                  value={formData.mobile_wallet}
                  onChange={(e) => setFormData({ ...formData, mobile_wallet: e.target.value })}
                  placeholder="e.g., Touch 'n Go"
                />
              </div>
            </div>
          </div>

          {/* Hierarchy & Commission */}
          <div className="space-y-4">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900">
              <Users className="w-4 h-4 text-purple-600" />
              Hierarchy & Commission
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Rank</label>
                <Select 
                  value={formData.agent_rank} 
                  onValueChange={(v) => setFormData({ ...formData, agent_rank: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="neg">NEG</SelectItem>
                    <SelectItem value="rm">RM</SelectItem>
                    <SelectItem value="avp">AVP</SelectItem>
                    <SelectItem value="svp">SVP</SelectItem>
                    <SelectItem value="evp">EVP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Upline</label>
                <Select 
                  value={formData.upline_id || 'none'} 
                  onValueChange={(v) => setFormData({ ...formData, upline_id: v === 'none' ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select upline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Upline (Top Level)</SelectItem>
                    {uplineOptions.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.agent_code} - {u.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Referred By Code</label>
                <Input
                  value={formData.referred_by_code}
                  onChange={(e) => setFormData({ ...formData, referred_by_code: e.target.value })}
                  placeholder="Enter referral code"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Commission %</label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.commission_percentage}
                  onChange={(e) => setFormData({ ...formData, commission_percentage: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">KYC Status</label>
                <Select 
                  value={formData.kyc_status} 
                  onValueChange={(v) => setFormData({ ...formData, kyc_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.full_time_agent}
                onCheckedChange={(checked) => setFormData({ ...formData, full_time_agent: checked })}
              />
              <label className="text-sm font-medium text-gray-700">Full-Time Agent</label>
            </div>
          </div>

          {/* Agent Codes (Read-Only) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Agent Codes (Read-Only)</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Agent Code:</span>
                <span className="ml-2 font-mono font-semibold text-purple-600">{agent.agent_code}</span>
              </div>
              <div>
                <span className="text-gray-500">Referral Code:</span>
                <span className="ml-2 font-mono">{agent.referral_code || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Update Agent'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Change Hierarchy Dialog Component
const ChangeHierarchyDialog = ({ open, onOpenChange, agent, allUsers, onSave }) => {
  const [newRank, setNewRank] = useState('');
  const [newUplineId, setNewUplineId] = useState('');
  const [uplineSearch, setUplineSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agent) {
      setNewRank(agent.agent_rank || agent.role || 'agent');
      setNewUplineId(agent.upline_id || '');
    }
  }, [agent]);

  if (!agent) return null;

  const currentUpline = agent.upline_id ? allUsers.find(u => u.id === agent.upline_id) : null;
  const newUpline = newUplineId ? allUsers.find(u => u.id === newUplineId) : null;

  const uplineOptions = allUsers.filter(u => {
    if (u.id === agent.id) return false;
    if (!['neg', 'rm', 'avp', 'svp', 'evp'].includes(u.role)) return false;
    if (uplineSearch) {
      const search = uplineSearch.toLowerCase();
      return u.full_name?.toLowerCase().includes(search) ||
             u.agent_code?.toLowerCase().includes(search) ||
             u.email?.toLowerCase().includes(search);
    }
    return true;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${agent.id}`, {
        agent_rank: newRank,
        role: newRank,
        upline_id: newUplineId || null,
      });
      toast.success('Hierarchy updated successfully');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update hierarchy');
    } finally {
      setSaving(false);
    }
  };

  const ranks = [
    { value: 'agent', label: 'Agent', icon: User },
    { value: 'rm', label: 'RM', icon: Award },
    { value: 'avp', label: 'AVP', icon: Star },
    { value: 'svp', label: 'SVP', icon: Crown },
    { value: 'evp', label: 'EVP', icon: Trophy },
  ];

  const getRankLabel = (rank) => {
    const r = ranks.find(r => r.value === rank);
    return r ? r.label : rank?.toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">
            <div>Change Upline & Rank</div>
            <div className="text-sm font-normal text-gray-500">
              {agent.full_name} ({agent.agent_code})
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-2">Current Status</h4>
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-gray-500">Rank:</span>
                <span className="ml-2 font-semibold">{getRankLabel(agent.agent_rank || agent.role)}</span>
              </div>
              <div>
                <span className="text-gray-500">Upline:</span>
                <span className="ml-2 font-semibold">
                  {currentUpline ? `${currentUpline.agent_code} - ${currentUpline.full_name}` : 'None'}
                </span>
              </div>
            </div>
          </div>

          {/* Select New Rank */}
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Select New Rank</h4>
            <div className="grid grid-cols-5 gap-2">
              {ranks.map((rank) => {
                const Icon = rank.icon;
                const isSelected = newRank === rank.value;
                return (
                  <button
                    key={rank.value}
                    onClick={() => setNewRank(rank.value)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50 text-purple-600' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                    data-testid={`rank-btn-${rank.value}`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-sm font-medium">{rank.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Select New Upline */}
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Select New Upline</h4>
            <Input
              placeholder="Search by name, code, or email..."
              value={uplineSearch}
              onChange={(e) => setUplineSearch(e.target.value)}
              className="mb-3"
            />
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <button
                onClick={() => setNewUplineId('')}
                className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 border-b ${
                  !newUplineId ? 'bg-purple-50' : ''
                }`}
                data-testid="upline-none"
              >
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">No Upline</p>
                  <p className="text-xs text-gray-500">Set as top-level agent</p>
                </div>
                {!newUplineId && (
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              {uplineOptions.slice(0, 15).map((u) => (
                <button
                  key={u.id}
                  onClick={() => setNewUplineId(u.id)}
                  className={`w-full flex items-center gap-3 p-3 hover:bg-gray-50 border-b last:border-b-0 ${
                    newUplineId === u.id ? 'bg-purple-50' : ''
                  }`}
                  data-testid={`upline-${u.id}`}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                    style={{ backgroundColor: getAvatarColor(u.full_name) }}
                  >
                    {u.full_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{u.full_name}</p>
                    <p className="text-xs text-gray-500">
                      {u.agent_code} • {(u.role || u.agent_rank)?.toUpperCase()} • {u.email}
                    </p>
                  </div>
                  {newUplineId === u.id && (
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Preview Changes */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="flex items-center gap-2 font-medium text-blue-800 mb-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Preview Changes
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Rank will change from <strong>{getRankLabel(agent.agent_rank || agent.role)}</strong> to <strong>{getRankLabel(newRank)}</strong></li>
              <li>• Upline will change to <strong>{newUpline ? `${newUpline.agent_code} - ${newUpline.full_name}` : 'None (Top Level)'}</strong></li>
            </ul>
            <p className="text-xs text-blue-600 mt-2">All related commissions will be automatically recalculated.</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Update Hierarchy'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Add Agent Dialog Component
const AddAgentDialog = ({ open, onOpenChange, allUsers, onSave }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    agent_code: '',
    agent_rank: 'agent',
    upline_id: '',
    password: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.full_name || !formData.email || !formData.agent_code) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/register', {
        ...formData,
        role: formData.agent_rank,
        password: formData.password || 'password123', // Default password
      });
      toast.success('Agent added successfully');
      onSave();
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        agent_code: '',
        agent_rank: 'agent',
        upline_id: '',
        password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add agent');
    } finally {
      setSaving(false);
    }
  };

  const uplineOptions = allUsers.filter(u => 
    ['neg', 'rm', 'avp', 'svp', 'evp'].includes(u.role)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Agent</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Full Name *</label>
            <Input
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Enter full name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Email *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Phone</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Enter phone"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Agent Code *</label>
            <Input
              value={formData.agent_code}
              onChange={(e) => setFormData({ ...formData, agent_code: e.target.value.toUpperCase() })}
              placeholder="e.g., NEG006"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Rank</label>
            <Select 
              value={formData.agent_rank} 
              onValueChange={(v) => setFormData({ ...formData, agent_rank: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="neg">NEG</SelectItem>
                <SelectItem value="rm">RM</SelectItem>
                <SelectItem value="avp">AVP</SelectItem>
                <SelectItem value="svp">SVP</SelectItem>
                <SelectItem value="evp">EVP</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Upline</label>
            <Select 
              value={formData.upline_id || 'none'} 
              onValueChange={(v) => setFormData({ ...formData, upline_id: v === 'none' ? '' : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select upline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Upline</SelectItem>
                {uplineOptions.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.agent_code} - {u.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Initial Password</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Leave empty for default"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Adding...' : 'Add Agent'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentManagement;
